"use client";

import { IconClock } from "@tabler/icons-react";
import { Reveal } from "../../../components/cambodia-food-expo/ui/Reveal";
import type { Localized, ScheduleItem } from "./types";
import { UI, useText } from "./shared";

const pad = (n: number) => String(n).padStart(2, "0");

/** Vertical timeline. Shows the time when known, otherwise a step number — never an invented time. */
export function EventTimeline({
  items,
  startAt = 1,
}: {
  items: ScheduleItem[];
  startAt?: number;
}) {
  return (
    <ol className="relative space-y-3 before:absolute before:bottom-6 before:left-[1.6rem] before:top-6 before:w-px before:bg-[linear-gradient(180deg,#f6cf63,rgba(246,207,99,0.15))] md:before:left-[2.1rem]">
      {items.map((item, i) => (
        <EventTimelineItem
          key={`${item.title.en}-${i}`}
          item={item}
          index={startAt + i}
        />
      ))}
    </ol>
  );
}

export function EventTimelineItem({
  item,
  index,
}: {
  item: ScheduleItem;
  index: number;
}) {
  const { text } = useText();
  return (
    <li className="relative flex gap-3 md:gap-5">
      <span className="relative z-10 flex size-[3.25rem] shrink-0 items-center justify-center rounded-full border border-[#f6cf63]/50 bg-[var(--event-card)] text-sm font-bold text-[var(--event-accent)] shadow-[0_0_0_4px_var(--event-page)] md:size-[4.25rem] md:text-base">
        {item.time ?? pad(index)}
      </span>
      <div className="event-schedule-card min-w-0 flex-1 rounded-[14px] border border-[var(--event-line)] px-4 py-3.5 transition hover:border-[#f6cf63]/40 md:px-5 md:py-4">
        <p className="text-[0.98rem] font-medium leading-7 text-[var(--event-ink)] md:text-[1.05rem] md:leading-8">
          {text(item.title)}
        </p>
        {item.durationMinutes ? (
          <p className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-[#f6cf63]/10 px-2.5 py-0.5 text-xs font-semibold text-[var(--event-accent)]">
            <IconClock className="size-3.5" aria-hidden="true" />
            {item.durationMinutes} {text(UI.minutes)}
          </p>
        ) : null}
      </div>
    </li>
  );
}

/** Long programs: consecutive items sharing a `group` become one chapter card. */
export function EventGroupedProgram({ items }: { items: ScheduleItem[] }) {
  const { text } = useText();
  const groups: {
    label: Localized | undefined;
    items: ScheduleItem[];
    startAt: number;
  }[] = [];
  items.forEach((item, i) => {
    const last = groups[groups.length - 1];
    if (last && last.label?.en === item.group?.en) last.items.push(item);
    else groups.push({ label: item.group, items: [item], startAt: i + 1 });
  });

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {groups.map((group, i) => (
        <Reveal
          key={`${group.label?.en ?? "group"}-${i}`}
          delay={(i % 2) * 0.06}
        >
          <section className="h-full rounded-[18px] border border-[var(--event-line)] bg-[var(--event-card)] p-4 md:p-6">
            {group.label && (
              <h5 className="mb-4 flex items-center gap-3 text-base font-semibold leading-relaxed text-[var(--event-accent)] md:text-lg">
                <span className="flex size-8 items-center justify-center rounded-full bg-[#f6cf63] text-sm font-bold text-[#041127]">
                  {i + 1}
                </span>
                {text(group.label)}
              </h5>
            )}
            <EventTimeline items={group.items} startAt={group.startAt} />
          </section>
        </Reveal>
      ))}
    </div>
  );
}
